import React from "react";
import { FaExclamationTriangle, FaCalendarCheck, FaMoneyBillWave } from "react-icons/fa";

/**
 * Shared report card — the commercial-voucher dossier used by the
 * InventoryManagement and Invoices queues.
 *
 * Visual language:
 *  - a status "spine" on the left edge encodes the workflow state at a glance
 *  - the document serial number is stamped as a monospace tabular chip
 *  - amounts sit in a tabular totals strip, like the foot of a paper invoice
 *  - color is never the only signal: every state also carries a text label
 */

const STATUS = {
  N: { label: "En cours", spine: "#B45309", tint: "#FEF6EC" },
  V: { label: "Validée", spine: "#236571", tint: "#ECF3F3" },
  A: { label: "Annulée", spine: "#9AA0A6", tint: "#F3F4F6" },
};

const NEUTRAL_SPINE = "#236571";

function ReportCard({
  report,
  documentNumber,
  status, // "N" | "V" | "A" | undefined
  docTypes = [],
  badges = [], // e.g. ["Facturation fin de mois"]
  totals = [], // [{ label, value, strong }] — rendered as a tabular strip
  onOpen,
  onValidate,
  onCancel,
  canUpdate = false,
  showActions = true,
  alertMessage = null,
}) {
  const state = STATUS[status];
  const spineColor = state ? state.spine : NEUTRAL_SPINE;

  // true when at least one product on the report was marked "Espèce" (cash) in the mobile app
  const hasEspece =
    report?.Rapport && typeof report.Rapport === "object"
      ? Object.values(report.Rapport).some(
          (categorie) =>
            Array.isArray(categorie?.produits) &&
            categorie.produits.some((produit) => produit?.espece === true)
        )
      : false;

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-[#E6E2D9] bg-[#FBFAF7] shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 dark:border-navy-700 dark:bg-navy-800">
      {/* status spine */}
      <span
        className="absolute inset-y-0 left-0 w-[5px]"
        style={{ backgroundColor: spineColor }}
        aria-hidden="true"
      />

      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-1 flex-col gap-3 py-4 pl-6 pr-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#236571]"
      >
        {/* client + serial stamp */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-[15px] font-bold leading-tight text-[#0F2B30] dark:text-white">
              {report?.InfoGenerale?.NomSociete}
            </p>
            {report?.InfoGenerale?.Adress ? (
              <p className="mt-1 truncate text-xs text-[#6B7770] dark:text-gray-400">
                {report.InfoGenerale.Adress}
              </p>
            ) : null}
          </div>

          {documentNumber ? (
            <span className="shrink-0 rounded-md bg-[#ECF3F3] px-2 py-1 font-mono text-xs font-semibold tabular-nums tracking-wider text-[#236571] dark:bg-navy-900 dark:text-indigo-200">
              {documentNumber}
            </span>
          ) : null}
        </div>

        {/* document-type chips + badge chips */}
        {docTypes.length > 0 || badges.length > 0 || hasEspece ? (
          <div className="flex flex-wrap gap-1.5">
            {docTypes.map((type) => (
              <span
                key={type}
                className="rounded border border-[#E6E2D9] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#6B7770] dark:border-navy-700 dark:text-gray-400"
              >
                {type}
              </span>
            ))}
            {badges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1 rounded bg-[#FEF6EC] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#B45309]"
              >
                <FaCalendarCheck size={10} />
                {badge}
              </span>
            ))}
            {hasEspece ? (
              <span
                className="inline-flex items-center gap-1 rounded bg-[#FEF6EC] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#B45309]"
                title="Espèce"
              >
                <FaMoneyBillWave size={10} />
                Espèce
              </span>
            ) : null}
          </div>
        ) : null}

        {/* meta row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#4B5A53] dark:text-gray-300">
          {report?.ModeOperation ? (
            <span>
              <span className="text-[#9AA7A0]">Opération</span> · {report.ModeOperation}
            </span>
          ) : null}
          {report?.CommercialName ? (
            <span>
              <span className="text-[#9AA7A0]">TCP</span> · {report.CommercialName}
            </span>
          ) : null}
          <span className="tabular-nums">
            {report?.CurrentDate} · {report?.CurrentTime}
          </span>
        </div>

        {/* totals strip — amounts read like the foot of a paper invoice */}
        {totals.length > 0 ? (
          <dl className="mt-1 space-y-0.5 border-t border-dashed border-[#E6E2D9] pt-2 dark:border-navy-700">
            {totals.map(({ label, value, strong }) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-3 text-xs"
              >
                <dt
                  className={`uppercase tracking-wide ${
                    strong
                      ? "font-semibold text-[#0F2B30] dark:text-white"
                      : "text-[#9AA7A0]"
                  }`}
                >
                  {label}
                </dt>
                <dd
                  className={`font-mono tabular-nums ${
                    strong
                      ? "text-sm font-bold text-[#0F2B30] dark:text-white"
                      : "text-[#4B5A53] dark:text-gray-300"
                  }`}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {/* escompte alert — focused, not alarming */}
        {alertMessage ? (
          <div className="flex items-center gap-1.5 rounded-md bg-[#FBEAE7] px-2 py-1 text-xs font-medium text-[#cf503f]">
            <FaExclamationTriangle size={12} />
            {alertMessage}
          </div>
        ) : null}
      </button>

      {/* footer: status + actions */}
      {showActions ? (
        <div className="flex items-center justify-between gap-2 border-t border-[#EFEBE2] py-3 pl-6 pr-5 dark:border-navy-700">
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold"
            style={{ color: spineColor }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: spineColor }}
            />
            {state ? state.label : ""}
          </span>

          {status === "N" && canUpdate ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onValidate}
                className="rounded-lg bg-[#236571] px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#1b4f58] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#236571] focus-visible:ring-offset-1"
              >
                Valider
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-transparent px-3 py-1.5 text-xs font-semibold text-[#cf503f] transition-colors hover:bg-[#FBEAE7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cf503f]"
              >
                Annuler
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default ReportCard;
